import { AuthStore } from 'auth/storage/auth-storage-manager';
import {
  AuthenticationMethod,
  CreateGatewayAuthMethodProps
} from 'auth/types/authentication-method.type';
import { generateCodeChallenge, generateCodeVerifier } from 'auth/auth-helpers/pkce-codes';
import { AuthenticationOptions } from 'auth/types/authentication-options.type';
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import Qs from 'qs';
import { differenceInSeconds } from 'date-fns';
import { TokenResponse } from 'auth/types/token-response.type';
import { getPostAuthUserInfo } from 'auth/auth-helpers/auth-helper';
import { getLocalJwtFromSessionStorage, initLocalJwtToken } from 'auth';
import { getAccess } from 'auth/auth-helpers/get-access';
import { replaceWithDynamicHost } from 'utils/api/api-host-utils';

const SESSION_AUTH_IDENTIFIER = 'userIdentifier';

const logout =
  ({ setToken, setSignedInUser, env, navigate }: CreateGatewayAuthMethodProps) =>
  async (): Promise<void> => {
    const clearSignedInUser = () => {
      AuthStore.setAuthToken(undefined);
      AuthStore.setUserInfo(undefined);
      setToken(undefined);
      setSignedInUser(undefined);
    };

    const authConfig = env.auth;
    const authMethod = authConfig.method;
    const requestUrl = replaceWithDynamicHost(env.apiUrl, env.apiHostMappings);

    const sessionToken = sessionStorage.getItem(SESSION_AUTH_IDENTIFIER);
    if (!sessionToken) return;

    const parsedSessionToken = JSON.parse(sessionToken);
    const idToken = parsedSessionToken.id_token;
    const accessTokenGuid = parsedSessionToken.access_token;

    const logoutRequest: AxiosRequestConfig = {
      url: `${requestUrl}/api/v2/securities/logout`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: getLocalJwtFromSessionStorage() ?? `Bearer ${accessTokenGuid}`
      },
      data: { idToken, authMethod }
    };

    const requestInstance = axios.create(logoutRequest);

    try {
      await requestInstance.request(logoutRequest);
    } finally {
      clearSignedInUser();
    }

    navigate(window.location.origin);
  };

const login =
  ({ setToken, setSignedInUser, env, navigate }: CreateGatewayAuthMethodProps) =>
  (authenticationOptions?: AuthenticationOptions): Promise<boolean> => {
    const token = AuthStore.getAuthToken();
    if (token) {
      const isTokenExpired = token.token_expiry_time < new Date();
      if (!isTokenExpired) {
        setToken(token);
        return Promise.resolve(true);
      }
    }

    const authSuccessRedirect = authenticationOptions?.authSuccessRedirect ?? '/';
    const codeVerifier = generateCodeVerifier();
    const codeChallenge = generateCodeChallenge(codeVerifier);
    AuthStore.setCodeVerifier(codeVerifier);
    AuthStore.setUserInfo(undefined);
    AuthStore.setAuthToken(undefined);
    setToken(undefined);
    setSignedInUser(undefined);

    const redirectUri = `${window.location.origin}${env.auth.gateway.redirectUriPath}`;

    let authUrl = replaceWithDynamicHost(env.auth.gateway.provider, env.apiHostMappings);
    authUrl = authUrl.includes('?') ? authUrl.concat('&') : authUrl.concat('?');
    authUrl = authUrl.concat('response_type=code');
    authUrl = authUrl.concat('&code_challenge_method=S256');
    authUrl = authUrl.concat(`&code_challenge=${codeChallenge}`);
    authUrl = authUrl.concat(`&client_id=${env.auth.gateway.clientId}`);
    authUrl = authUrl.concat(`&redirect_uri=${redirectUri}`);
    authUrl = authUrl.concat(`&state=${encodeURIComponent(authSuccessRedirect)}`);

    if (env.auth.gateway.scopes.length > 0) {
      const scopes = env.auth.gateway.scopes.join('%20');
      authUrl = authUrl.concat(`&scope=${scopes}`);
    }
    navigate(authUrl);
    return Promise.resolve(true);
  };

const getUserAccess =
  (props: CreateGatewayAuthMethodProps) =>
  async (token: TokenResponse | undefined): Promise<boolean> => {
    const getAccessSuccess = await getAccess({
      getSignedInUser: props.getSignedInUser,
      setSignedInUser: props.setSignedInUser,
      setCurrentAuthenticatedUserWithFeatures: props.setCurrentAuthenticatedUserWithFeatures,
      env: props.env,
      navigate: props.navigate
    })(token);
    if (!getAccessSuccess) {
      const logoutUser = logout(props);
      await logoutUser();
    }
    return getAccessSuccess;
  };

const refreshSession = (props: CreateGatewayAuthMethodProps) => async (): Promise<boolean> => {
  const { setToken, setSignedInUser, env } = props;
  const currentToken = AuthStore.getAuthToken();
  const logoutUser = logout(props);
  if (!currentToken) {
    await logoutUser();
    return false;
  }
  const secondsUntilExpiry = differenceInSeconds(currentToken.token_expiry_time, new Date());
  const isTokenExpired = secondsUntilExpiry < env.auth.refreshIntervalSeconds;
  if (!isTokenExpired) return true;

  const onRefreshSuccess = async (refreshedToken: TokenResponse): Promise<boolean> => {
    const tokenExpiryTime = new Date();
    tokenExpiryTime.setSeconds(tokenExpiryTime.getSeconds() + refreshedToken.expires_in);
    refreshedToken.token_expiry_time = tokenExpiryTime;

    const userInfo = await getPostAuthUserInfo(
      refreshedToken.access_token,
      replaceWithDynamicHost(authConfig.gateway.userInfoProvider, env.apiHostMappings)
    );

    if (!userInfo) {
      await logoutUser();
      return false;
    }

    AuthStore.setAuthToken(refreshedToken);
    setToken(refreshedToken);
    AuthStore.setUserInfo(userInfo);
    setSignedInUser(userInfo);

    const localJwtProvider = props.env.auth.gateway.jwtProvider;

    if (localJwtProvider) {
      const localJwt = await initLocalJwtToken({
        gatewayToken: refreshedToken,
        localJwtProvider: replaceWithDynamicHost(
          props.env.auth.gateway.jwtProvider,
          env.apiHostMappings
        ),
        bffApiUrl: replaceWithDynamicHost(props.env.apiUrl, props.env.apiHostMappings)
      });
      if (!localJwt) return false;
    }

    return true;
  };

  const authConfig = env.auth;

  const tokenRefreshRequest: AxiosRequestConfig = {
    url: replaceWithDynamicHost(authConfig.gateway.tokenProvider, env.apiHostMappings),
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data: Qs.stringify({
      grant_type: 'refresh_token',
      client_id: authConfig.gateway.clientId,
      refresh_token: currentToken.refresh_token
    })
  };
  const tokenRefreshInstance = axios.create(tokenRefreshRequest);
  let refreshedToken: TokenResponse | undefined = undefined;

  try {
    const tokenRefreshResponse: AxiosResponse<TokenResponse> =
      await tokenRefreshInstance.request(tokenRefreshRequest);
    if (tokenRefreshResponse.status != 200) {
      await logoutUser();
      return false;
    }
    refreshedToken = tokenRefreshResponse.data;
  } catch (_error) {
    await logoutUser();
    return false;
  }

  if (!refreshedToken) {
    await logoutUser();
    return false;
  }

  return await onRefreshSuccess(refreshedToken);
};

const retrieveUserInfo =
  ({ env, setSignedInUser }: CreateGatewayAuthMethodProps) =>
  async (accessToken?: string): Promise<boolean> => {
    const userInfoToken = accessToken ?? AuthStore.getAuthToken()?.access_token;
    if (!userInfoToken) return false;
    const userInfo = await getPostAuthUserInfo(
      userInfoToken,
      replaceWithDynamicHost(env.auth.gateway.userInfoProvider, env.apiHostMappings)
    );
    if (!userInfo) return false;
    AuthStore.setUserInfo(userInfo);
    setSignedInUser(userInfo);
    return true;
  };

export const createGatewayAuthMethod = (
  props: CreateGatewayAuthMethodProps
): AuthenticationMethod => {
  return {
    logout: logout(props),
    login: login(props),
    refreshSessionIfExpired: refreshSession(props),
    retrieveUserInfo: retrieveUserInfo(props),
    getAccess: getUserAccess(props),
    setToken: props.setToken,
    getSignedInUser: props.getSignedInUser,
    identificationState: props.identificationState
  };
};
